import React, { useCallback } from 'react';
import { Alert, Box, css, FormControl, IconButton, Snackbar, TextField } from '@mui/material';
import ExpandIcon from '@mui/icons-material/Expand';
import ReplayIcon from '@mui/icons-material/Replay';
import { useRef, useEffect, RefObject, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSeismicStore } from '../stores/useSeismicStore';
import { useMapStore } from '../stores/useMapStore';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

const containerStyle = css({
    display: 'flex',
    maxWidth: 300,
    padding: '10px',
    '& .MuiFormControl-root': {
        margin: '0px',
        '& .MuiOutlinedInput-notchedOutline': {
            '& legend': {
                width: '64px',
            }
        }
    },
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            borderColor: 'white',
            borderWidth: '1px',
            borderRadius: '5px'
        }
    }
});

export interface SeismicChangeScaleProps {
    canFit: boolean;
}

export function SeismicChangeScale ({canFit}: SeismicChangeScaleProps) {
    const refScaleX = useRef<HTMLInputElement>(null);
    const refScaleY = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();

    const { surveyMetadata, scale, setScaleX, setScaleY, setWarningMessage, seismicCalculator } = useSeismicStore(state => ({
        surveyMetadata: state.surveyMetadata,
        scale: state.scale,
        setScaleX: state.setScaleX,
        setScaleY: state.setScaleY,
        setWarningMessage: state.setWarningMessage,
        seismicCalculator: state.calculator,
    }));

    const featureFlags = useFeatureFlags().featureFlags?.seismic2D;

    const { resolutions } = useMapStore(state => ({
        resolutions: state.resolutions,
    }));

    useEffect(() => {
        if (refScaleX.current){
            refScaleX.current.value = scale.x.toString();
        }

        if (refScaleY.current){
            refScaleY.current.value = scale.y.toString();
        }

    }, [scale?.x, scale?.y]);

    const changeValue = (type: Type) => {
        if (type === Type.x){
            setValue(refScaleX, setScaleX);
        }
        else if (type === Type.y){
            setValue(refScaleY, setScaleY);
        }
    };

    const setValue = (ref: RefObject<HTMLInputElement>, setScale: (value: number) => void) => {
        let value = 1;
        if (ref.current){
            value = parseFloat(ref.current.value);
        }

        if (value < 0) {
            value = Math.abs(value);
            setWarningMessage(t('scaleNegativeValue'));
        }

        if (isNaN(value)) {
            value = 1;
        }

        setScale(value);
    };

    const fitHorizontal = useCallback(() => {
        if (!!surveyMetadata && !!seismicCalculator) {
            const numTraces = seismicCalculator.getTotalTraces(surveyMetadata);
            const calcXScale = window.innerWidth / numTraces * resolutions[0];
            setScaleX(calcXScale);
        }
    }, [resolutions, seismicCalculator, setScaleX, surveyMetadata]);

    const resetToDefault = () => {
        setScaleX(surveyMetadata?.DefaultScaleX ?? 1);
    };

    return (
        <React.Fragment>
            <div css={containerStyle}>
                { canFit ?
                    <Fragment>
                        <IconButton style={{width: '32px', height: '32px',marginRight: '5px'}} id='fit-horizontal' title={'Fit the seismic on the screen'} size='medium' onClick={() => fitHorizontal()}  color='inherit'>
                            <ExpandIcon color='primary' sx={{ transform: 'rotate(90deg)' }} />
                        </IconButton>
                        {
                            featureFlags?.AllowHorizontalFit
                            &&
                            <IconButton style={{width: '32px', height: '32px',marginRight: '5px'}} id='fit-horizontal' title={'Reset scale'} size='medium' onClick={() => resetToDefault()}  color='inherit'>
                                <ReplayIcon color='primary' />
                            </IconButton>
                        }
                    </Fragment>
                    : null
                }
                <FormControl fullWidth>
                    <TextField inputRef={refScaleX} label={'Horizontal'} variant='outlined' onBlur={() => changeValue(Type.x)} style={{width:'90px', marginRight: '10px'}} />
                </FormControl>
                <FormControl fullWidth>
                    <TextField inputRef={refScaleY} label={'Vertical'} variant='outlined' onBlur={() => changeValue(Type.y)}  style={{width:'90px'}}/>
                </FormControl>
            </div>
        </React.Fragment>

    );
}

enum Type {
    x = 1,
    y = 2
}